import { SavingsType, TargetingType } from './promotion.interface';
import { SortDescriptor } from '@progress/kendo-data-query';
import { User } from './user-interface';
import {
    AdvancedFilters,
    CheckboxGroupSelection,
    TextSearchSelection
} from 'quotient-ng-core';

export interface Event {
    id: number;
    partner_event_code: string;
    name: string;
    assigned_to?: User;
    approver?: User;
    purchase_qty: number;
    event_type?: string;
    created_by: { id: number; name: string };
    start_date: string;
    end_date: string;
    time_zone: string;
    brands: string;
    description: string;
    gtins?: { gtin: string }[];
    pids?: { pid: number }[];
    targeting_type?: TargetingType;
    savings_type: SavingsType;
    savings_value?: number;
    process_stage?: EventStage;
    manufacturer?: { id: number; name: string };
    partner: { id: number; name: string };
    matched_offers?: EventMatchedOffer[];
    tracking_property: { created_date: string; last_updated_date: string };
    offer_in_production_date?: string;
}

export enum EventStage {
    INCOMPLETE = 'Incomplete',
    AWAITING_APPROVAL = 'AwaitingForApproval',
    APPROVED = 'Approved'
}

export enum EventMatchedOfferType {
    MANUAL = 'Manual',
    AUTO = 'Auto'
}

export interface EventMatchedOffer {
    offer_id?: number;
    promotion_id?: number;
    offer_code?: string;
    match_type?: EventMatchedOfferType;
    sort_order?: number;
    selected?: boolean;
    matched_gtins?: string[];
    attribute?: MatchedOfferAttributes;
    offerStage?: MatchedOfferStatus;
}

export type OfferAttributeName =
    | 'brand'
    | 'description'
    | 'start_date'
    | 'end_date'
    | 'gtins'
    | 'manufacturer'
    | 'pids'
    | 'purchase_qty'
    | 'savings_type'
    | 'targeting_type';
export type MatchedOfferAttributeRelevance = 'None' | 'Low' | 'Strong';
export type MatchedOfferAttributes = {
    [K in OfferAttributeName]: {
        current_offer_value: string | number;
        matchtime_offer_value: string | number;
        timezone?: string;
        relevance: MatchedOfferAttributeRelevance;
    };
};

export enum EventOfferAction {
    ADD = 'add',
    DELETE = 'delete',
    LINK = 'link',
    UNLINK = 'unlink'
}

export enum EventAction {
    RESET = 'reset',
    APPROVE = 'approve',
    DELETE = 'delete',
    EXPORT = 'export',
    EXPORT_ALL = 'exportAll',
    CREATE = 'create',
    CLONE = 'clone',
    EDIT = 'edit',
    EXPAND_ALL = 'expandAll',
    COLLAPSE_ALL = 'collapseAll'
}

export interface EventSearchCriteria {
    advancedFilters: AdvancedFilters<AdvancedFilterField>;
    basicFilters: BasicFilters;
    keyword: TextSearchSelection;
}

export interface BasicFilters {
    groupList: CheckboxGroupSelection[];
    checkedCount: number;
}

export interface EventListResponse {
    events: Event[];
    total: number;
}

export enum AdvancedFilterField {
    BRAND = 'event_brand',
    CREATE_BY = 'created_by',
    ASSIGNED_TO = 'assigned_to',
    OFFER_IN_PRODUCTION_BY = 'event_offer_in_production_by',
    CREATED_DATE = 'create_date',
    SAVINGS_TYPE = 'event_savings_type',
    EVENT_TYPE = 'event_type',
    MANUFACTURER = 'event_manufacturer',
    START_DATE = 'start_date',
    END_DATE = 'end_date',
    PID = 'event_pid'
}

export enum EventSearchType {
    EVENT = 'Event',
    OFFER = 'Offer',
    SEARCH_ALL = 'All'
}

export enum EventTimeZone {
    Central = 'US/Central',
    Eastern = 'US/Eastern',
    Hawaii = 'US/Hawaii',
    Mountain = 'US/Mountain',
    Pacific = 'US/Pacific',
    UTC = 'UTC'
}

// TODO: Move this interface to another shared file in common
export interface GridPagination {
    pageSize: number;
    skip: number;
}

export interface EventSearchRequestPayload {
    searchCriteria: EventSearchCriteria;
    pagination?: GridPagination;
    sortDescriptor?: SortDescriptor;
}

/**
 * Enum mapping for matching offer list's "Offer Status" column.
 * Same as the mapping in campaigns app.
 */
export enum MatchedOfferStatus {
    Approved = 'Approved',
    Incomplete = 'InProgress',
    Rejected = 'Rejected',
    RolledOut = 'Launched',
    SentForApproval = 'InProgress',
    Shutoff = 'Shutoff',
    Cancelled = 'Cancelled',
    Deleted = 'Deleted',
    Draft = 'Draft',
    Draft_2 = 'Draft'
}

export class BulkEditModel {
    assigned_to: User;
    approver: User;
    brands: string;
    description: string;
    manufacturer: object;
}
